import React, { memo, useState, useCallback, useRef, useEffect } from "react";
import { shallowEqual } from "react-redux";
import {
  store as reduxStore,
  DefaultRootState,
  useAppDispatch,
  useAppSelector,
} from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import theme from "theme";
import StopIcon from "@mui/icons-material/Stop";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { IconButton, Button, Tooltip } from "@mui/material";
import Slider from "@mui/material/Slider";
import { getIsRoleAudience } from "stores/modules/entities.room.members/selectors";
import { useToCDNUrl, useIsCDNUrl } from "hooks/useCDN";
import { linearToScaledDb, scaledDbToLinear } from "modules/sound";
import { AudioPlayer } from "modules/audioplayer";
import { loadSoundMasterUrl } from "stores/modules/entities.rooms/operations";
import { useTranslation } from "react-i18next";

type MediaProps = {
  roomId: string;
  uid: string;
};

const stopPropagation = (e) => e.stopPropagation();

const Media = (props: MediaProps) => {
  const {
    mediaName,
    mediaUrl,
    mediaVolume,
    mediaRepeat,
    soundName,
    soundUrl,
    soundVolume,
    soundRepeat,
    previewUrl,
    previewVolume,
    previewRepeat,
    muted,
    volume,
    blocked, // for video ad
    enableCrossfade,
    crossfadeDuration,
  } = useAppSelector(selectMediaSettings, shallowEqual);
  const isRoleAudience = useAppSelector(getIsRoleAudience);
  const dispatch = useAppDispatch();
  const toCDNUrl = useToCDNUrl();
  const isCDNUrl = useIsCDNUrl();
  const setVolume = React.useCallback(
    (v: number) => {
      const volume = scaledDbToLinear(v);
      dispatch(
        store.appStateMutate((state) => (state.roomMediaVolume = volume))
      );
    },
    [dispatch]
  );
  const setMuted = useCallback(
    (muted: boolean) => {
      dispatch(store.appStateMutate((state) => (state.muted = muted)));
    },
    [dispatch]
  );

  const media = useRef<AudioPlayer | null>(null);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (media.current) {
        media.current.unload();
        media.current = null;
      }
    };
  }, []);

  useEffect(() => {
    dispatch(loadSoundMasterUrl("media"));
    dispatch(loadSoundMasterUrl("sound"));
  }, [dispatch]);

  useEffect(() => {
    if (blocked) return;

    const state = reduxStore.getState();
    const mediaSettings = selectMediaSettings(state);
    const enableGoCdn = state.app.state.config.enabledGoCdnOnAudio;
    const previousPlayer = media.current;

    if (!mediaUrl) {
      // URLがクリアされた場合
      if (previousPlayer) {
        media.current = null;
        if (enableCrossfade) {
          previousPlayer.fadeOut(crossfadeDuration).then(() => {
            previousPlayer.unload();
          });
        } else {
          previousPlayer.unload();
        }
      }
      return;
    }

    const shouldCrossfade = enableCrossfade && previousPlayer;

    const newPlayer = new AudioPlayer({
      src: toCDNUrl(mediaUrl, { enableGoCdn }),
      enableWebAudioGain: isCDNUrl(mediaUrl),
      autoplay: !shouldCrossfade,
      loop: mediaSettings.mediaRepeat,
      volume: Number(mediaSettings.mediaVolume) * mediaSettings.volume,
      muted: mediaSettings.muted,
    });

    // 新しいプレイヤーを先に設定
    media.current = newPlayer;

    if (shouldCrossfade) {
      // クロスフェード: 旧プレイヤーをフェードアウト、新プレイヤーをフェードイン
      previousPlayer.fadeOut(crossfadeDuration).then(() => {
        previousPlayer.unload();
      });
      newPlayer.play().then(() => {
        newPlayer.fadeIn(crossfadeDuration);
      });
    } else {
      // 通常の切り替え（autoplay=trueで既に再生開始している）
      if (previousPlayer) {
        previousPlayer.unload();
      }
    }
  }, [
    mediaUrl,
    blocked,
    enableCrossfade,
    crossfadeDuration,
    toCDNUrl,
    isCDNUrl,
  ]);
  useEffect(() => {
    if (media.current) {
      media.current.volume(Number(mediaVolume) * volume);
      media.current.mute(muted);
      media.current.loop(mediaRepeat);
    }
  }, [mediaVolume, volume, mediaRepeat, muted]);

  const sound = useRef<AudioPlayer | null>(null);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (sound.current) {
        sound.current.unload();
        sound.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (blocked) return;

    const state = reduxStore.getState();
    const mediaSettings = selectMediaSettings(state);
    const enableGoCdn = state.app.state.config.enabledGoCdnOnAudio;
    const previousPlayer = sound.current;

    if (!soundUrl) {
      // URLがクリアされた場合
      if (previousPlayer) {
        sound.current = null;
        if (enableCrossfade) {
          previousPlayer.fadeOut(crossfadeDuration).then(() => {
            previousPlayer.unload();
          });
        } else {
          previousPlayer.unload();
        }
      }
      return;
    }

    const shouldCrossfade = enableCrossfade && previousPlayer;

    const newPlayer = new AudioPlayer({
      src: toCDNUrl(soundUrl, { enableGoCdn }),
      enableWebAudioGain: isCDNUrl(soundUrl),
      autoplay: !shouldCrossfade, // クロスフェード時は手動で再生、それ以外は自動再生
      loop: mediaSettings.soundRepeat,
      volume: Number(mediaSettings.soundVolume) * mediaSettings.volume,
      muted: mediaSettings.muted,
    });

    // 新しいプレイヤーを先に設定
    sound.current = newPlayer;

    if (shouldCrossfade) {
      // クロスフェード: 旧プレイヤーをフェードアウト、新プレイヤーをフェードイン
      previousPlayer.fadeOut(crossfadeDuration).then(() => {
        previousPlayer.unload();
      });
      newPlayer.play().then(() => {
        newPlayer.fadeIn(crossfadeDuration);
      });
    } else {
      // 通常の切り替え（autoplay=trueで既に再生開始している）
      if (previousPlayer) {
        previousPlayer.unload();
      }
    }
  }, [
    soundUrl,
    blocked,
    enableCrossfade,
    crossfadeDuration,
    toCDNUrl,
    isCDNUrl,
  ]);
  useEffect(() => {
    if (sound.current) {
      sound.current.volume(Number(soundVolume) * volume);
      sound.current.mute(muted);
      sound.current.loop(soundRepeat);
    }
  }, [soundVolume, volume, soundRepeat, muted]);

  const preview = useRef<AudioPlayer | null>(null);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (preview.current) {
        preview.current.unload();
        preview.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (previewUrl && !blocked) {
      const state = reduxStore.getState();
      const mediaSettings = selectMediaSettings(state);
      const enableGoCdn = state.app.state.config.enabledGoCdnOnAudio;

      // 前のプレイヤーをクリーンアップ
      if (preview.current) {
        preview.current.unload();
      }

      const player = new AudioPlayer({
        src: toCDNUrl(previewUrl, { enableGoCdn }),
        enableWebAudioGain: isCDNUrl(previewUrl),
        autoplay: true,
        loop: mediaSettings.previewRepeat,
        volume: Number(mediaSettings.previewVolume) * mediaSettings.volume,
        muted: mediaSettings.previewMuted,
      });

      preview.current = player;
    } else if (!previewUrl && preview.current) {
      // URLがクリアされた場合
      preview.current.unload();
      preview.current = null;
    }
  }, [previewUrl, blocked, toCDNUrl, isCDNUrl]);
  useEffect(() => {
    if (preview.current) {
      preview.current.volume(Number(previewVolume) * volume);
      preview.current.loop(previewRepeat);
    }
  }, [previewVolume, volume, previewRepeat]);

  const $ = useEnhance(props);
  const [t] = useTranslation();

  return (
    <Container onDoubleClick={stopPropagation}>
      <Controls>
        <VolumeSlider
          value={linearToScaledDb(volume)}
          color="primary"
          onChange={(_, value) =>
            setVolume(Array.isArray(value) ? value[0] : value)
          }
          min={0.0}
          max={1}
          step={0.01}
          aria-label={t("音量")}
        />
        <Actions>
          <Tooltip title={muted ? t("ミュート中") : ""}>
            <IconButton
              color={muted ? "primary" : "default"}
              size="small"
              onClick={() => setMuted(!muted)}
              aria-label={t("ミュート")}
              aria-pressed={muted}
            >
              {muted ? (
                <VolumeOffIcon fontSize="small" />
              ) : (
                <VolumeUpIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={t("BGMを停止する")}>
            <IconButton
              size="small"
              onClick={$.onStop}
              disabled={isRoleAudience}
              aria-label={t("BGMを停止する")}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={`${mediaName || "NOTITLE"}\nvol:${mediaVolume} loop:${
              mediaRepeat ? "on" : "off"
            }`}
          >
            <Button
              size="small"
              color={mediaUrl ? "primary" : undefined}
              onClick={$.onClickMedia01}
              disabled={isRoleAudience}
            >
              BGM01
            </Button>
          </Tooltip>
          <Tooltip
            title={`${soundName || "NOTITLE"}\nvol:${soundVolume} loop:${
              soundRepeat ? "on" : "off"
            }`}
          >
            <Button
              size="small"
              color={soundUrl ? "primary" : undefined}
              onClick={$.onClickMedia02}
              disabled={isRoleAudience}
            >
              BGM02
            </Button>
          </Tooltip>
        </Actions>
      </Controls>
    </Container>
  );
};

const selectMediaSettings = (state: DefaultRootState) => {
  const room = store.getCurrentRoom(state);
  return {
    mediaName: room?.mediaName,
    mediaUrl: room?.mediaUrl,
    mediaVolume: room?.mediaVolume ?? 0.5,
    mediaRepeat: room?.mediaRepeat !== undefined ? room.mediaRepeat : true,
    soundName: room?.soundName,
    soundUrl: room?.soundUrl,
    soundVolume: room?.soundVolume ?? 0.5,
    soundRepeat: room?.soundRepeat !== undefined ? room.soundRepeat : true,
    previewUrl: state.app.state.previewMediaUrl,
    previewVolume: state.app.state.previewMediaVolume,
    previewRepeat: state.app.state.previewMediaLoop,
    previewMuted: state.app.state.muted,
    muted:
      state.app.state.muted ||
      state.app.state.previewMediaUrl !== null ||
      store.getAppState(state, "openVideoPlayer"),
    volume: state.app.state.roomMediaVolume,
    blocked:
      store.getOpenInformation(state) ||
      store.getAppState(state, "openVideoPlayer"),
    enableCrossfade: room?.enableCrossfade ?? false,
    crossfadeDuration: room?.crossfadeDuration ?? 2.0,
  };
};

const Container = styled.div`
  position: absolute;
  top: 64px;
  right: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  transition: background 200ms ease-out;
  &:hover {
    background: rgba(0, 0, 0, 0.4);
  }
  ${theme.breakpoints.down("sm")} {
    top: 56px;
    left: auto;
    right: 0;
  }
`;

const VolumeSlider = styled(Slider)`
  margin-left: 8px;
  width: 80px;
`;

const Controls = styled.div`
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  > * + * {
    margin-left: 8px;
  }
`;

const Actions = styled.div`
  margin-left: 8px;
`;

const useEnhance = (props: MediaProps) => {
  const [playing, setPlaying] = useState(true);
  const player1 = useRef<any>(null); // TODO: any
  const player2 = useRef<any>(null); // TODO: any
  const dispatch = useAppDispatch();

  const onEnd1 = useCallback(() => {
    if (player1.current && !player1.current.props.loop) {
      dispatch(
        store.updateRoom(props.roomId, {
          mediaName: "",
          mediaUrl: null,
          mediaRef: null,
          mediaRepeat: true,
          mediaType: "",
          mediaVolume: 0,
        })
      );
    }
  }, [props.roomId, player1, dispatch]);
  const onEnd2 = useCallback(() => {
    if (player2.current && !player2.current.props.loop) {
      dispatch(
        store.updateRoom(props.roomId, {
          soundName: "",
          soundUrl: null,
          soundRef: null,
          soundRepeat: true,
          soundVolume: 0,
        })
      );
    }
  }, [props.roomId, player2, dispatch]);

  const onPause = useCallback(() => {
    setPlaying(false);
  }, []);

  const onStart = useCallback(() => {
    setPlaying(true);
  }, []);

  const onStop = useCallback(() => {
    dispatch(
      store.updateCurrentRoomField({
        mediaName: "",
        mediaUrl: null,
        mediaRef: null,
        mediaRepeat: true,
        mediaType: "",
        mediaVolume: 0,
        soundName: "",
        soundUrl: null,
        soundRef: null,
        soundRepeat: true,
        soundVolume: 0,
      })
    );
  }, [dispatch]);

  const onClickMedia01 = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomMedia = true;
        state.openRoomMediaDir = "bgm"; // todo: rename
        state.enabledUserMediaLoader = true;
      })
    );
  }, [dispatch]);

  const onClickMedia02 = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomMedia = true;
        state.openRoomMediaDir = "sound"; // todo: rename
        state.enabledUserMediaLoader = true;
      })
    );
  }, [dispatch]);

  return {
    onStart,
    onPause,
    onStop,
    onClickMedia01,
    onClickMedia02,
    playing,
    player1,
    player2,
    onEnd1,
    onEnd2,
  };
};

export default memo(Media);
